import React, { useRef, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShoppingCart, User, Home, Package, MapPin, LogOut, Search, X } from 'lucide-react';

function Header({ onSignInClick, onCartClick, user, onLogout, cartCount }) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");

  useEffect(() => {
    setSearchQuery(searchParams.get("search") || "");
  }, [searchParams]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/');
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 flex items-center justify-center px-4 sm:px-10 lg:px-20 py-3 bg-[#F3ECE1] border-b border-[#E3D4C1] shadow-xs">
      <div className="flex items-center justify-between w-full max-w-7xl gap-4">
        
        {/* Left Side: Brand Logo & Navigation */}
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center cursor-pointer hover:opacity-90 transition">
            {/* 🌟 VIRTUAL CROP WRAPPER: Limits the outer frame while zooming into the logo text */}
            <div className="w-32 h-12 overflow-hidden relative flex items-center justify-center">
              <img 
                src="/logo_white.webp" 
                alt="Mridanga" 
                className="absolute w-34 max-w-none h-36 object-contain mix-blend-multiply"
                style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
              />
            </div>
          </Link>
          
          {/* Subtle Aesthetic Divider Line between Logo and Home */}
          <div className="hidden sm:block h-5 w-px bg-[#E3D4C1]" />
          
          <Link 
            to="/" 
            className="hidden sm:flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-[#5A5145] hover:text-[#9A7B56] transition-all h-9 px-3 rounded-md hover:bg-[#EADFCF]"
          >
            <Home className="w-3.5 h-3.5 text-[#8A7E70]" />
            <span>Home</span>
          </Link>
        </div>

        {/* Sleek Minimalist Search Bar */}
        <form onSubmit={handleSearchSubmit} className="flex-1 max-w-lg hidden md:block mx-2">
          <div className="flex items-center bg-[#FDFDFB] border border-[#E3D4C1] rounded-md h-9.5 w-full pl-3.5 pr-1 transition-all duration-300 focus-within:border-[#9A7B56] focus-within:shadow-xs">
            <Search className="w-3.5 h-3.5 text-[#A6998A] mr-2.5 shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent text-[#2C2722] placeholder:text-[#A6998A] focus:outline-none text-xs tracking-wide font-light h-full"
              placeholder="Search premium acoustic collections..."
            />
            {searchQuery && (
              <button type="button" onClick={clearSearch} className="p-1.5 text-[#A6998A] hover:text-[#2C2722] transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <button type="submit" className="bg-[#EADFCF] hover:bg-[#9A7B56] text-[#5A5145] hover:text-white text-[10px] uppercase tracking-widest px-3.5 h-7.5 rounded-sm transition-all ml-1 font-semibold">
              Go
            </button>
          </div>
        </form>

        {/* Right Actions Pane */}
        <div className="flex items-center gap-1">
          <Link to="/" className="flex sm:hidden items-center justify-center rounded-md h-9 w-9 text-[#5A5145] hover:text-[#9A7B56] hover:bg-[#EADFCF] transition-colors">
            <Home className="w-4 h-4" />
          </Link>

          {/* Cart Interaction Wrapper */}
          <div className="relative">
            <button onClick={onCartClick} className="flex items-center justify-center rounded-md h-9 gap-2 text-[11px] font-semibold uppercase tracking-widest px-3 text-[#5A5145] hover:text-[#9A7B56] hover:bg-[#EADFCF] transition-all">
              <ShoppingCart className="w-4 h-4 text-[#8A7E70]" />
              <span className="hidden sm:inline">Cart</span>
            </button>
            {cartCount > 0 && (
              <span className="absolute top-0 right-1 bg-[#9A7B56] text-white text-[9px] font-bold rounded-full min-w-[15px] h-3.5 px-1 flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </div>

          {/* Account Profile Wrapper */}
          <div className="relative" ref={menuRef}>
            <button onClick={() => setShowMenu(!showMenu)} className="flex items-center justify-center rounded-md h-9 gap-2 text-[11px] font-semibold uppercase tracking-widest px-3 text-[#5A5145] hover:text-[#9A7B56] hover:bg-[#EADFCF] transition-all">
              <User className="w-4 h-4 text-[#8A7E70]" />
              <span className="hidden sm:inline">{user ? "Account" : "Sign In"}</span>
            </button>

            {showMenu && (
              <div className="absolute right-0 mt-2 w-52 bg-[#FDFDFB] border border-[#E3D4C1] rounded-md shadow-md z-50 py-1 font-sans overflow-hidden">
                {user ? (
                  <>
                    <div className="px-4 py-3 border-b border-[#E3D4C1] bg-[#F3ECE1]">
                      <p className="text-[9px] text-[#A6998A] uppercase tracking-widest font-semibold">Verified Musician</p>
                      <p className="text-xs text-[#2C2722] truncate font-light mt-0.5">{user.email || 'Customer'}</p>
                    </div>
                    <button onClick={() => { setShowMenu(false); navigate('/orders'); }} className="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-xs font-light uppercase tracking-wider text-[#5A5145] hover:bg-[#F3ECE1] hover:text-[#9A7B56] transition-colors">
                      <Package className="w-3.5 h-3.5 text-[#8A7E70]" />
                      <span>My Orders</span>
                    </button>
                    <button onClick={() => { setShowMenu(false); navigate('/addresses'); }} className="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-xs font-light uppercase tracking-wider text-[#5A5145] hover:bg-[#F3ECE1] hover:text-[#9A7B56] transition-colors">
                      <MapPin className="w-3.5 h-3.5 text-[#8A7E70]" />
                      <span>Saved Addresses</span>
                    </button>
                    <div className="border-t border-[#E3D4C1] my-1"></div>
                    <button onClick={() => { setShowMenu(false); onLogout(); }} className="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-amber-800 hover:bg-orange-50/50 transition-colors">
                      <LogOut className="w-3.5 h-3.5 text-amber-700" />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <button onClick={() => { setShowMenu(false); onSignInClick(); }} className="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#5A5145] hover:bg-[#F3ECE1] hover:text-[#9A7B56] transition-colors">
                    <User className="w-3.5 h-3.5 text-[#8A7E70]" />
                    <span>Sign In / Register</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;